// Web push for the installed app.
//
// iOS is the fussy one: it only supports this from iOS 16.4, only once the app has been added to
// the Home Screen, and only when permission is asked for from a real tap. Everything here is built
// around those three rules.
import { isStandalone } from './pwa'

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function isIOS() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS reports itself as a Mac, but a touchscreen gives it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

// On iOS, Safari exposes no push API at all until the app is on the Home Screen — so "unsupported"
// there usually means "not installed yet" rather than "your phone can't do this".
export function needsInstallFirst() {
  return isIOS() && !isStandalone()
}

export function permission() {
  return pushSupported() ? Notification.permission : 'unsupported'
}

// Read per request, never cached. The <meta name="csrf-token"> in the document is baked in at the
// initial page load and goes stale the moment the session rotates (signing in) or the SPA
// navigates. Inertia's Rails adapter refreshes an XSRF-TOKEN cookie on every protected response
// instead, and its middleware copies X-XSRF-TOKEN onto X-CSRF-Token for Rails to verify.
function xsrfHeader() {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)

  // Rack escapes the cookie value, and the token is base64 — so + / = arrive percent-encoded.
  return { 'X-XSRF-TOKEN': match ? decodeURIComponent(match[1]) : '' }
}

function toParams(subscription) {
  const json = subscription.toJSON()

  return { endpoint: json.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth }
}

// The VAPID public key travels as base64url text and has to reach subscribe() as bytes.
function applicationServerKey(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)

  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)))
}

async function registration() {
  return navigator.serviceWorker.ready
}

async function existingSubscription() {
  if (!pushSupported()) return null

  return (await registration()).pushManager.getSubscription()
}

async function sendToServer(subscription) {
  const response = await fetch('/push_subscription', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...xsrfHeader() },
    body: JSON.stringify(toParams(subscription)),
  })

  if (!response.ok) throw new Error(`Could not save the subscription (${response.status})`)
}

// Must be called from a click handler: iOS ignores a permission prompt that isn't tied to a tap.
export async function enablePush(vapidPublicKey) {
  if (!pushSupported()) throw new Error('This browser does not support notifications.')
  if (!vapidPublicKey) throw new Error('Notifications are not set up on this server yet.')

  const granted = await Notification.requestPermission()
  if (granted !== 'granted') return false

  let subscription = await existingSubscription()

  // A subscription is bound to the key it was created with. After the server rotates its VAPID
  // pair, the old one can never be delivered to again — and subscribe() refuses to replace it —
  // so drop it and start over.
  if (subscription && !boundTo(subscription, vapidPublicKey)) {
    await subscription.unsubscribe()
    subscription = null
  }

  subscription =
    subscription ||
    (await (await registration()).pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(vapidPublicKey),
    }))

  await sendToServer(subscription)

  return true
}

function boundTo(subscription, base64) {
  const existing = subscription.options?.applicationServerKey
  if (!existing) return true

  const current = new Uint8Array(existing)
  const wanted = applicationServerKey(base64)

  return current.length === wanted.length && current.every((byte, index) => byte === wanted[index])
}

export async function disablePush() {
  const subscription = await existingSubscription()
  if (!subscription) return

  await fetch(`/push_subscription?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
    method: 'DELETE',
    headers: xsrfHeader(),
  })

  await subscription.unsubscribe()
}

export async function isSubscribed() {
  return permission() === 'granted' && Boolean(await existingSubscription())
}

// Browsers rotate push endpoints on their own schedule, and the service worker can't tell the
// server about a new one. Re-registering whatever the device holds now keeps the two in step.
export async function syncSubscription() {
  if (permission() !== 'granted') return

  const subscription = await existingSubscription()
  if (!subscription) return

  try {
    await sendToServer(subscription)
  } catch (error) {
    console.warn('Could not refresh the push subscription', error)
  }
}
